import axios from "axios";
import { useRouter } from "next/router";
import { sanitizeUrl } from "@braintree/sanitize-url";
import LogoIcon from "../../components/Icons/LogoIcon";
import Head from "next/head";
import { useUserContext } from "../../contexts/UserProvider";
import { LoginIllustration } from "../../components/Illustrations/LoginIllustration";

import { FieldValues } from "react-hook-form";
import { parseErrorsToArray } from "../../utils/parseErrors";
import { cn } from "ui";
import { useRedirectToHomeIfAuthorized } from "../../hooks/useRedirectToHomeIfAuthorized";
import { useState } from "react";
import { LoginForm } from "../../features/login/LoginForm";
import { OTPForm } from "../../features/login/OTPForm";
import { safeLocalStorage } from "../../utils/safeLocalStorage";
import { getUserRole } from "../../utils/getUserRole";
import { identifyThirdParties } from "../../utils/thirdParties/identify";
import type { LoginResponse } from "../../features/login/types";
import { getEmailFingerprint } from "../../utils/getEmailFingerprint";
import { toast } from "ui";
import { analytics } from "../../features/analytics/analyticsManager";

export default function Login() {
  const router = useRouter();
  const [authenticationStep, setAuthenticationStep] = useState<
    "LOGIN" | "VERIFY"
  >("LOGIN");

  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>("");
  const hasCheckedAuth = useRedirectToHomeIfAuthorized();

  const {
    setId: contextSetId,
    setRole: contextSetRole,
    setName: contextSetName,
    setEmail: contextSetEmail,
    setOrganizationName: contextSetOrgName,
    setOrganizationId: contextSetOrgId,
    setIsOnboarded: contextSetIsOnboarded,
  } = useUserContext();

  const handleLoginSubmit = async ({ email, password }: FieldValues) => {
    const fingerprint = getEmailFingerprint(email);

    return axios
      .post<LoginResponse>(`${process.env.NEXT_PUBLIC_API_BASE}/users/login/`, {
        email,
        password,
        fingerprint,
      })
      .then((response) => {
        const { otp_required } = response.data.data;
        if (otp_required) {
          setDeviceToken(response.data.data.device_token);
          setUserEmail(email);
          setAuthenticationStep("VERIFY");
          return;
        }

        handleSuccessfulAuthentication(response.data.data);
      })
      .catch((err) => {
        console.log(err);
        const [errorMessage] = parseErrorsToArray(err);
        return { errors: { root: errorMessage || "Something went wrong." } };
      });
  };

  const handleSuccessfulAuthentication = ({
    user,
    token,
  }: Extract<LoginResponse["data"], { otp_required: false }>) => {
    const { returnUrl } = router.query;

    const {
      id,
      is_org_user,
      is_mh_superuser,
      email,
      name,
      organization_id,
      organization_name,
      persona,
      has_completed_dashboard_onboarding,
      trial_end_date,
      date_joined,
      is_mh_user,
      organization_created_date,
    } = user;

    safeLocalStorage.setItem("__AT", token.access);
    safeLocalStorage.setItem("__RT", token.refresh);

    analytics.identify({
      name,
      email,
      organization_name,
      persona,
    });
    analytics.track("user_logged_in", {
      name,
      email,
      organization_name,
      persona,
    });

    contextSetId(`${id}`);
    contextSetRole(
      getUserRole({
        is_org_user,
        is_mh_superuser,
        is_mh_user,
      })
    );
    contextSetName(name);
    contextSetEmail(email);
    contextSetOrgName(organization_name);
    contextSetOrgId(`${organization_id}`);
    contextSetIsOnboarded(has_completed_dashboard_onboarding);

    // Sanitize all navigation from urls
    const sanitizedReturnUrl = sanitizeUrl(returnUrl as string);
    const shouldRedirectToHome = sanitizedReturnUrl === "about:blank";

    identifyThirdParties({
      id,
      is_org_user,
      is_mh_superuser,
      email,
      name,
      organization_id,
      organization_name,
      persona,
      has_completed_dashboard_onboarding,
      trial_end_date,
      date_joined,
      organization_created_date,
    });

    toast.success("You're now securely signed in.");
    router.push(shouldRedirectToHome ? "/" : sanitizedReturnUrl);
  };

  return (
    <div className="flex min-h-screen">
      <Head>
        <title>{process.env.NEXT_PUBLIC_APP_TITLE} | Login</title>
        <meta name="description" content="Login to dashboard" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <div></div>
      <div className="hidden w-[47%] flex-col bg-[rgba(var(--neutral-light-800))] p-8 lg:flex">
        <LogoIcon className="text-white" />
        <div className="h-full ">
          <div className="flex h-full items-center justify-center">
            <LoginIllustration />
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-center bg-foundations-subtlest p-9">
        <div
          className={cn(
            "mx-auto w-full max-w-xl rounded-lg border border-foundations-subtle bg-foundations-default p-10",
            !hasCheckedAuth && "pointer-events-none"
          )}
        >
          <>
            {authenticationStep === "LOGIN" && (
              <LoginForm onSubmit={handleLoginSubmit} />
            )}

            {authenticationStep === "VERIFY" && (
              <OTPForm
                deviceToken={deviceToken!}
                userEmail={userEmail}
                onAuthenticate={handleSuccessfulAuthentication}
              />
            )}
          </>
        </div>
      </div>
    </div>
  );
}

Login.publicPath = true;
